import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

const MIN_CHECK_INTERVAL_MS = 60 * 1000;
const LOAD_WINDOW_MS = 15 * 1000;
let lastCheck = 0;
let applying = false;

/**
 * Silent auto-update: checks for a new published version on load, when the
 * app regains focus/visibility and every 30 min; when one is found it is
 * activated and the page reloads by itself (no prompt).
 */
export function useAppUpdate() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      if (!r) return;
      const check = () => {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        const now = Date.now();
        if (now - lastCheck < MIN_CHECK_INTERVAL_MS) return;
        lastCheck = now;
        r.update().catch(() => {});
      };
      check();
      setInterval(check, 30 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('focus', check);
    },
    onRegisterError(error) {
      console.error('Service Worker registration error:', error);
    },
  });

  useEffect(() => {
    if (!needRefresh || applying) return;
    const apply = () => {
      if (applying) return;
      applying = true;
      void updateServiceWorker(true);
    };
    // Reload only right after load or while the tab is in the background, so
    // nobody loses a half-filled form; otherwise wait until the tab is hidden.
    const justLoaded = typeof performance !== 'undefined' && performance.now() < LOAD_WINDOW_MS;
    if (justLoaded || document.visibilityState === 'hidden') {
      apply();
      return;
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') apply();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [needRefresh, updateServiceWorker]);

  const dismissUpdate = () => setNeedRefresh(false);
  const dismissOfflineReady = () => setOfflineReady(false);
  const applyUpdate = () => updateServiceWorker(true);

  return {
    needRefresh,
    offlineReady,
    applyUpdate,
    dismissUpdate,
    dismissOfflineReady,
  };
}
